import { useState, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Camera, Check, ArrowLeft, ArrowRight, User, Sun, Moon, HelpCircle } from "lucide-react";
import { useLocation } from "wouter";
import { useToast } from "@/hooks/use-toast";
import { useTheme } from "@/lib/theme";
import { MirrorCard } from "@/components/MirrorCard";
import { AtmosphericPage } from "@/components/MirrorAtmosphere";

export default function Settings() {
  const [, setLocation] = useLocation();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { theme, toggleTheme } = useTheme();

  const { data: user, isLoading } = useQuery<any>({
    queryKey: ["/api/auth/me"],
  });

  const [name, setName] = useState<string | null>(null);
  const [picture, setPicture] = useState<string | null | undefined>(undefined);
  const [saving, setSaving] = useState(false);

  const displayName = name ?? user?.displayName ?? "";
  const profilePic = picture !== undefined ? picture : user?.profilePicture;

  const handlePhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast({ title: "Please select an image file", variant: "destructive" });
      return;
    }

    if (file.size > 1.5 * 1024 * 1024) {
      toast({ title: "Image too large. Please use one under 1.5MB.", variant: "destructive" });
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setPicture(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const body: Record<string, any> = {};
      if (name !== null) body.displayName = name;
      if (picture !== undefined) body.profilePicture = picture;
      if (Object.keys(body).length === 0) {
        setSaving(false);
        return;
      }
      await apiRequest("PATCH", "/api/auth/profile", body);
      queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] });
      toast({ title: "Profile updated" });
    } catch (err) {
      toast({ title: "Failed to save", variant: "destructive" });
    }
    setSaving(false);
  };

  const hasChanges = name !== null || picture !== undefined;

  if (isLoading) {
    return (
      <AtmosphericPage contained atmosphere={{ scrimVariant: "content", opacityScale: 0.4 }} contentClassName="flex items-center justify-center h-full gap-1.5">
        <div>
          {[0, 1, 2].map(i => (
            <div key={i} className="w-2 h-2 rounded-full" style={{ backgroundColor: "var(--accent-text-soft)", animation: `gentle-pulse 1.4s ease-in-out ${i * 0.2}s infinite` }} />
          ))}
        </div>
      </AtmosphericPage>
    );
  }

  return (
    <AtmosphericPage contained atmosphere={{ scrimVariant: "content", opacityScale: 0.4 }} contentClassName="flex flex-col h-full px-6 py-5">
      <div style={{ borderBottom: "1px solid var(--border-light)" }}>
        <div className="flex items-center justify-between gap-3">
          <div>
            <button
              onClick={() => setLocation("/")}
              className="touch-target text-mirror-text-muted"
              data-testid="button-back-home"
            >
              <ArrowLeft size={16} />
            </button>
            <h1
              className="text-xl"
              style={{ color: "var(--fg-raw)", fontWeight: 500, fontFamily: "var(--font-display)" }}
              data-testid="text-settings-heading"
            >
              Settings
            </h1>
          </div>
          {hasChanges && (
            <button
              onClick={handleSave}
              disabled={saving}
              className="touch-target flex items-center gap-1.5 px-4 py-1.5 rounded-lg transition-all duration-200 disabled:opacity-60"
              style={{
                background: "var(--accent-bg-strong)",
                border: "1px solid var(--accent-border)",
                color: "var(--accent-color)",
                fontFamily: "var(--font-display)",
              }}
              data-testid="button-save-profile"
            >
              <Check size={13} />
              {saving ? "Saving..." : "Save"}
            </button>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto max-w-md mx-auto px-6 py-10">
        <div>
          <div className="flex flex-col items-center mb-10">
            <button
              onClick={() => fileInputRef.current?.click()}
              className="relative group mb-4"
              data-testid="button-change-photo"
            >
              {profilePic ? (
                <img
                  src={profilePic}
                  alt=""
                  className="w-24 h-24 rounded-full object-cover"
                  style={{ border: "3px solid var(--accent-border)" }}
                  data-testid="img-profile-picture"
                />
              ) : (
                <div
                  className="w-24 h-24 rounded-full flex items-center justify-center"
                  style={{
                    background: "var(--surface-2)",
                    border: "3px solid var(--border-hairline)",
                  }}
                >
                  <User size={32} className="text-mirror-text-muted" />
                </div>
              )}
              <div
                className="absolute bottom-0 right-0 w-8 h-8 rounded-full flex items-center justify-center transition-opacity group-hover:opacity-100"
                style={{
                  background: "var(--accent-bg-strong)",
                  border: "2px solid var(--page-bg)",
                  opacity: 0.7,
                }}
              >
                <Camera size={13} style={{ color: "var(--accent-color)" }} />
              </div>
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handlePhotoSelect}
              data-testid="input-profile-photo"
            />
            <p
              style={{
                color: "var(--text-muted)",
                fontFamily: "var(--font-mono)",
                fontSize: "var(--type-label-size)",
                letterSpacing: "var(--type-label-tracking)",
                textTransform: "uppercase",
              }}
            >
              Tap to {profilePic ? "change" : "add"} photo
            </p>
          </div>

          {/* PROFILE section: 20px between cards */}
          <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
            <MirrorCard label="NAME" testId="card-name">
              <input
                type="text"
                value={displayName}
                onChange={e => setName(e.target.value)}
                placeholder="How should Fractal address you?"
                className="w-full bg-transparent text-base outline-none"
                style={{
                  color: "var(--fg-raw)",
                  fontFamily: "var(--font-display)",
                }}
                data-testid="input-display-name"
              />
            </MirrorCard>

            <MirrorCard label="EMAIL" testId="card-email">
              <div
                style={{
                  color: "var(--text-muted)",
                  fontFamily: "var(--font-mono)",
                  fontSize: "var(--type-label-size)",
                  letterSpacing: "var(--type-label-tracking)",
                  textTransform: "uppercase",
                }}
                data-testid="text-profile-email"
              >
                {user?.email || ""}
              </div>
            </MirrorCard>
          </div>

          {/* APPEARANCE section: 32px from previous section */}
          <div style={{ marginTop: "32px" }}>
            <MirrorCard
              label="APPEARANCE"
              meta={theme === "dark" ? "DARK" : "LIGHT"}
              icon={
                theme === "dark" ? (
                  <Moon size={16} style={{ color: "var(--accent-color)" }} />
                ) : (
                  <Sun size={16} style={{ color: "var(--accent-color)" }} />
                )
              }
              testId="card-appearance"
            >
              <div className="flex items-center justify-between">
                <span
                  style={{
                    color: "var(--fg-raw)",
                    fontFamily: "var(--font-display)",
                    fontSize: "0.9375rem",
                    fontWeight: 400,
                  }}
                >
                  {theme === "dark" ? "Dark mode" : "Light mode"}
                </span>
                <button
                  onClick={toggleTheme}
                  data-testid="button-toggle-theme"
                  className="touch-target relative flex items-center justify-center bg-transparent"
                >
                  <span
                    className="relative block h-6 w-12 rounded-full transition-colors duration-300"
                    style={{
                      background: theme === "dark" ? "var(--accent-color)" : "var(--border-strong)",
                    }}
                  >
                    <span
                      className="absolute top-0.5 block h-5 w-5 rounded-full transition-transform duration-300"
                      style={{
                        background: "var(--page-bg)",
                        transform: theme === "dark" ? "translateX(26px)" : "translateX(2px)",
                        boxShadow: "var(--control-shadow)",
                      }}
                    />
                  </span>
                </button>
              </div>
            </MirrorCard>
          </div>

          <div style={{ marginTop: "20px" }}>
            <MirrorCard
              label="FRACTAL GUIDE"
              icon={<HelpCircle size={16} style={{ color: "var(--accent-color)" }} />}
              testId="card-mirror-guide"
            >
              <button
                onClick={() => window.dispatchEvent(new CustomEvent("mirror-retake-tutorial"))}
                className="touch-target flex items-center justify-between w-full text-left"
                data-testid="button-reopen-tutorial"
              >
                <span
                  style={{
                    color: "var(--fg-raw)",
                    fontFamily: "var(--font-reflective)",
                    fontSize: "0.9375rem",
                    fontWeight: 400,
                  }}
                >
                  Replay the 30-second introduction
                </span>
                <ArrowRight size={14} style={{ color: "var(--text-muted)" }} />
              </button>
            </MirrorCard>
          </div>
        </div>
      </div>
    </AtmosphericPage>
  );
}
